import {useCallback,useEffect,useState,type FormEvent} from 'react';
import {SKU_LABELS} from '../lib/supplier-pricing';
import {OWNER_REPORTED_FACTORY_COSTS_USD,OWNER_COST_NOTE_DATE,supplierCostIntakeCsv} from '../lib/supplier-cost-intake';
import {reviewSupplierCsv,type SupplierImportReview} from '../lib/supplier-cost-import';
type Tier={minimumQuantity:number;purchaseCents:number};
type Term={sku:string;supplierName:string;supplierSku:string;moq:number;casePack:number;
 currency:string;tiers:Tier[];sourceNote:string;effectiveOn:string;expiresOn:string;
 status:'DRAFT'|'APPROVED'|'DISABLED';version:number;approvedAt:string|null;updatedAt:string};
type Audit={sku:string;version:number;action:string;occurred_at:string};
const LABEL:Record<string,string>={DRAFT:'待审核',APPROVED:'已审核',DISABLED:'已停用'};
export default function SupplierPricingManager({accessKey}:{accessKey:string}){
 const [terms,setTerms]=useState<Term[]>([]),[audit,setAudit]=useState<Audit[]>([]);
 const [sku,setSku]=useState('P1-S3'),[supplier,setSupplier]=useState(''),[supplierSku,setSupplierSku]=useState('');
 const [moq,setMoq]=useState(''),[casePack,setCasePack]=useState(''),[source,setSource]=useState('');
 const [effective,setEffective]=useState(''),[expiry,setExpiry]=useState('');
 const [tiers,setTiers]=useState(''),[busy,setBusy]=useState(false),[message,setMessage]=useState(''),[error,setError]=useState('');
 const [intakeRaw,setIntakeRaw]=useState(''),[intakeReview,setIntakeReview]=useState<SupplierImportReview|null>(null);
 const selected=terms.find(x=>x.sku===sku);
 // Coverage means only an approved and currently dated SKU/size baseline.
 // It is not verification of color, freight, customs, duties or profit.
 const todayUTC=new Date().toISOString().slice(0,10);
 const coverage=Object.entries(SKU_LABELS).map(([code,name])=>{
  const row=terms.find(t=>t.sku===code);
  const current=row?.status==='APPROVED'&&row.effectiveOn<=todayUTC&&row.expiresOn>=todayUTC;
  const label=current?'已审核且有效':!row?'尚未录入':row.status==='APPROVED'?'已过期或未生效':row.status==='DRAFT'?'待审核':'已停用';
  return {code,name,current,label};
 });
 const readyCount=coverage.filter(x=>x.current).length;
 const refresh=useCallback(async()=>{
  const r=await fetch('/api/admin/supplier-pricing',{headers:{'x-admin-key':accessKey}});
  const data=await r.json() as {ok?:boolean;terms?:Term[];audit?:Audit[];error?:string};
  if(!r.ok||!data.ok)throw Error(data.error||'无法读取供应商资料');
  setTerms(data.terms||[]);setAudit(data.audit||[]);
 },[accessKey]);
 useEffect(()=>{void refresh().catch(e=>setError(String(e.message||e)))},[refresh]);
 useEffect(()=>{
  const r=terms.find(x=>x.sku===sku);
  setSupplier(r?.supplierName||'');setSupplierSku(r?.supplierSku||'');
  setMoq(r?String(r.moq):'');setCasePack(r?String(r.casePack):'');
  setSource(r?.sourceNote||'');setEffective(r?.effectiveOn||'');setExpiry(r?.expiresOn||'');
  setTiers((r?.tiers||[]).map(t=>String(t.minimumQuantity)+','+(t.purchaseCents/100).toFixed(2)).join('\n'));
 },[sku,terms]);
 // A local worksheet download only. It does not insert or approve supplier
 // data in D1, and the three owner-reported factory prices stay unverified.
 function downloadIntakeSheet(){
  const blob=new Blob(['\uFEFF',supplierCostIntakeCsv()],{type:'text/csv;charset=utf-8'});
  const url=URL.createObjectURL(blob);
  const link=document.createElement('a');
  link.href=url;link.download='mingeagle-supplier-cost-intake-20261016.csv';
  document.body.appendChild(link);link.click();link.remove();URL.revokeObjectURL(url);
 }
 async function previewIntake(file?:File){
  setError('');setMessage('');setIntakeRaw('');setIntakeReview(null);
  if(!file)return;
  try{
   if(file.size>120000)throw Error('CSV 文件超过 120 KB，请使用已导出的标准 11 SKU 模板');
   const raw=await file.text();
   const result=reviewSupplierCsv(raw,terms.map(t=>t.sku));
   setIntakeRaw(raw);setIntakeReview(result);
  }catch(e){setError('CSV 导入预检失败：'+(e instanceof Error?e.message:'无法读取文件'))}
 }
 async function importIntakeDrafts(){
  if(!intakeRaw||!intakeReview||busy)return;
  setBusy(true);setError('');setMessage('');
  try{
   // The server independently validates the original CSV and existing SKUs
   // immediately before one transactional, draft-only batch.
   if(intakeReview.errors.length||!intakeReview.drafts.length)
    throw Error('预检未通过或没有可导入草稿，请先修改 CSV。');
   if(!window.confirm('将 '+intakeReview.drafts.length+' 个供应商核实数据作为一个整体保存为待审核草稿。\\n任一 SKU 冲突将拒绝整批，不批准成本、不修改报价或付款。确定继续吗？'))return;
   const reply=await fetch('/api/admin/supplier-pricing',{
    method:'POST',headers:{'content-type':'application/json','x-admin-key':accessKey},
    body:JSON.stringify({action:'IMPORT_CSV_DRAFTS',csv:intakeRaw})
   });
   const response=await reply.json() as {ok?:boolean;error?:string;status?:string;imported?:number;approved?:number;issues?:string[]};
   if(!reply.ok||!response.ok||response.status!=='DRAFT'||response.approved!==0||
      response.imported!==intakeReview.drafts.length)
    throw Error([response.error,...(response.issues||[])].filter(Boolean).join('；')||'供应商采购草稿整体导入失败');
   setMessage('已一次性保存 '+response.imported+' 条待审核供应商草稿，自动批准 0 条。请逐项核对原始供应商报价后单独审批。');
   setIntakeRaw('');setIntakeReview(null);
  }catch(e){
   setError('批量导入未被确认为成功：'+(e instanceof Error?e.message:'未知错误')+'。请刷新目录核对，再决定是否重新导入。');
  }finally{
   try{await refresh()}catch(e){setError(prev=>prev+' 目录刷新失败：'+String(e))}
   setBusy(false);
  }
 }
 async function save(e:FormEvent<HTMLFormElement>){
  e.preventDefault();setBusy(true);setError('');setMessage('');
  try{
   const tierRows=tiers.trim()?tiers.trim().split(/\r?\n/).map(line=>{
    const parts=line.split(',').map(z=>z.trim());
    if(parts.length!==2)throw Error('阶梯价格每行必须为 数量,USD单价');
    return {minimumQuantity:parts[0],unitCostUSD:parts[1]};
   }):[];
   await mutate({action:'SAVE_DRAFT',sku,expectedVersion:selected?.version||0,supplierName:supplier,
    supplierSku,moq,casePack,sourceNote:source,effectiveOn:effective,expiresOn:expiry,tiers:tierRows});
   setMessage('草稿保存成功。之前的审核会失效；检查原始供应商报价文件后才能批准。');
  }catch(e){setError(e instanceof Error?e.message:'保存失败')}finally{setBusy(false)}
 }
 async function mutate(data:Record<string,unknown>){
  const r=await fetch('/api/admin/supplier-pricing',{method:'POST',
   headers:{'content-type':'application/json','x-admin-key':accessKey},
   body:JSON.stringify(data)});
  const v=await r.json() as {ok?:boolean;error?:string};
  if(!r.ok||!v.ok)throw Error(v.error||'操作失败');
  await refresh();
 }
 async function statusChange(action:'APPROVE'|'DISABLE'){
  if(!selected)return;
  if(!window.confirm(action==='APPROVE'
   ?'确认已人工核对供应商正式采购报价、SKU 对应、MOQ、整箱件数、阶梯采购成本以及有效日期？批准只供内部参考，不会发送客户报价。'
   :'停用这份供应商采购资料？历史报价不受影响。'))return;
  setBusy(true);setError('');setMessage('');
  try{
   await mutate({action,sku,expectedVersion:selected.version,confirmed:true});
   setMessage(action==='APPROVE'?'已标记审核通过；仍不得直接作为客户销售价。':'已停用；不会影响既有客户报价。');
  }catch(e){setError(e instanceof Error?e.message:'操作失败')}finally{setBusy(false)}
 }
 return <div style={{display:'grid',gap:18}}>
  <section className="panel" style={{padding:18}}>
   <div className="panel-head"><h2>供应商采购价资料库</h2><span>内部数据 · 人工来源审查 · 按 SKU 管理</span></div>
   <p>只记录由供应商提供并由员工确认的采购成本，不能以官网零售价推算。不支持币种换算（当前仅 USD），也不会自动修改对外销售价格、客户报价或支付数据。产品 SKU 为产品系列+尺寸，具体颜色和配置仍需逐单核对。</p>
   {error&&<p role="alert" className="form-status error">{error}</p>}
   {message&&<p role="status" className="form-status success">{message}</p>}
   <section aria-label="供应商SKU采购成本覆盖情况" className="panel" style={{padding:12,margin:'12px 0'}}>
    <h3>供应商真实采购价覆盖：{readyCount} / {coverage.length} 个 SKU</h3>
    <p><strong>经营者曾提供的参考出厂成本：</strong>{OWNER_COST_NOTE_DATE} 口述记录，7 号植绒套装 $21、7 号布套套装 $23、7 号加重植绒篮球 $32。<strong>未核对供应商原始报价，不代表当前有效成本或到门总成本，也不会自动填入采购条款或计入“已审核”数量。</strong></p>
    <button type="button" className="button secondary small" onClick={downloadIntakeSheet}>下载 11 SKU 采购成本核实表（CSV）</button>
    <p style={{fontSize:12,color:'#667085'}}>表格仅在这 3 个 SKU 的“经营者参考价”栏填写已提供数字；实际供应商价、MOQ、装箱、日期、海运 DDP、关税、凭证和审批字段一律留空，必须凭真实单据完成。</p>
    <p>下表只判断 SKU/尺寸是否存在已审核且在有效期内的实际供应商报价。绿色状态不代表颜色、包装、头程、清关、关税或利润已经审核。点击“填写”可选择对应 SKU 并录入真实来源。</p>
    <div className="table-wrap"><table><thead><tr><th>SKU</th><th>型号</th><th>经营者参考出厂价（未核实）</th><th>成本依据状态</th><th>操作</th></tr></thead><tbody>
     {coverage.map(row=><tr key={row.code}><td>{row.code}</td><td>{row.name}</td><td>{OWNER_REPORTED_FACTORY_COSTS_USD[row.code]===undefined?'尚无参考':`${OWNER_REPORTED_FACTORY_COSTS_USD[row.code].toFixed(2)} · 待供应商核对`}</td><td>{row.label}</td>
       <td><button type="button" className="table-action" disabled={busy} onClick={()=>setSku(row.code)}>填写 / 核对</button></td></tr>)}
    </tbody></table></div>
   </section>
   <section className="panel" aria-label="批量核实供应商 CSV 导入" style={{padding:14,margin:'12px 0'}}>
    <h3>批量导入采购核实表 · 仅保存草稿</h3>
    <p>下载上方 11 SKU 表格后，只在 <strong>Supplier_Confirmed_Unit_Cost_USD</strong> 填写有供应商凭证支持的美元采购价，补齐供应商、MOQ、装箱、报价日期、有效期及供应商报价编号。历史参考价栏不会参与任何导入。</p>
    <label>选择已填写的 CSV 文件（预览不会修改数据库）
      <input type="file" accept=".csv,text/csv" disabled={busy}
       onChange={e=>void previewIntake(e.target.files?.[0])}/>
    </label>
    {intakeReview&&<div role="status" style={{marginTop:12}}>
     <p><strong>预检：</strong>可创建草稿 {intakeReview.drafts.length} 条；
      未填写确认价而跳过 {intakeReview.skipped.length} 条；
      错误 {intakeReview.errors.length} 条。</p>
     {intakeReview.drafts.length>0&&<p>拟导入 SKU：{intakeReview.drafts.map(x=>x.sku).join('、')}。导入后必须人工审核，绝不会自动标记 APPROVED。</p>}
     {intakeReview.errors.length>0&&<ul>{intakeReview.errors.map((reason,i)=><li key={i}>{reason}</li>)}</ul>}
     <button type="button" className="button secondary" disabled={busy||!!intakeReview.errors.length||!intakeReview.drafts.length}
      onClick={()=>void importIntakeDrafts()}>{busy?'正在核对 / 导入…':'确认导入为待审核草稿'}</button>
    </div>}
    <p style={{fontSize:12,color:'#667085'}}>不会覆盖已有 SKU（包括已审核版本），也不会把历史口述价、海运或关税自动当成已批准成本。后台会重新核验整个 CSV 并事务保存；任何 SKU 冲突或写入异常都不会接受部分成功。</p>
   </section>
   <form onSubmit={e=>void save(e)} className="quote-form">
    <div className="quote-form-grid">
     <label className="span-2">内部产品 SKU<select value={sku} onChange={e=>setSku(e.target.value)}>
      {Object.entries(SKU_LABELS).map(([id,name])=><option key={id} value={id}>{id} · {name}</option>)}
     </select></label>
     <label>供应商名称<input value={supplier} onChange={e=>setSupplier(e.target.value)} required placeholder="依据实际供应商资料填写"/></label>
     <label>供应商型号（选填）<input value={supplierSku} onChange={e=>setSupplierSku(e.target.value)} placeholder="实际型号 / 代码"/></label>
     <label>MOQ 最低采购件数<input type="number" min="1" max="100000" step="1" value={moq} onChange={e=>setMoq(e.target.value)} required/></label>
     <label>每箱件数 / 整箱倍数<input type="number" min="1" max="100000" step="1" value={casePack} onChange={e=>setCasePack(e.target.value)} required/></label>
     <label>成本报价生效日（UTC）<input type="date" value={effective} onChange={e=>setEffective(e.target.value)} required/></label>
     <label>供应商报价到期日（UTC）<input type="date" value={expiry} onChange={e=>setExpiry(e.target.value)} required/></label>
     <label className="span-2">采购成本阶梯（仅 USD，每行：最低采购件数,采购单价）<textarea rows={5} value={tiers} onChange={e=>setTiers(e.target.value)} placeholder={'100,4.50\n500,3.90'}/><small>上方仅为格式示例，不是 MING EAGLE 的真实采购价格。第一行数量必须等于 MOQ，后续数量递增，最多 12 档。允许先保存无价格草稿，但不能批准。</small></label>
     <label className="span-2">报价依据 / 审核来源<textarea rows={3} value={source} onChange={e=>setSource(e.target.value)} required placeholder="供应商报价编号、报价日期、联系人确认渠道等（请勿粘贴访问密钥）"/></label>
    </div>
    <div className="quote-form-actions">
     <button className="button" type="submit" disabled={busy}>{busy?'正在保存…':'保存为待审核草稿'}</button>
     {selected?.status==='DRAFT'&&<button className="button secondary" type="button" disabled={busy} onClick={()=>void statusChange('APPROVE')}>确认审核并启用</button>}
     {selected&&selected.status!=='DISABLED'&&<button className="button secondary" type="button" disabled={busy} onClick={()=>void statusChange('DISABLE')}>停用资料</button>}
    </div>
   </form>
   <p style={{fontSize:12,color:'#667085'}}>当前 SKU：{sku} · {selected?'版本 '+selected.version+' / '+(LABEL[selected.status]||selected.status):'尚未配置'}。修改已审核资料会自动恢复待审核状态。审批仅代表当前管理员人工确认，不代表已完成双人复核。</p>
  </section>
  <section className="panel" style={{padding:18}}>
   <div className="panel-head"><h2>已录入的供应商条款（{terms.length}）</h2><button className="button secondary small" disabled={busy} onClick={()=>void refresh().catch(e=>setError(String(e.message||e)))}>刷新</button></div>
   <div className="table-wrap"><table><thead><tr><th>SKU</th><th>供应商</th><th>MOQ / 整箱</th><th>成本阶梯</th><th>到期（UTC）</th><th>状态</th><th>版本</th></tr></thead><tbody>
    {terms.map(t=><tr key={t.sku}><td>{t.sku}</td><td>{t.supplierName}</td><td>{t.moq} / {t.casePack}</td><td>{t.tiers.length} 档（USD）</td><td>{t.expiresOn}</td><td>{LABEL[t.status]||t.status}</td><td>{t.version}</td></tr>)}
    {!terms.length&&<tr><td colSpan={7}>尚未录入真实供应商价格；系统不会预填虚构成本。</td></tr>}
   </tbody></table></div>
  </section>
  <section className="panel" style={{padding:18}}>
   <h2>最近版本与操作记录</h2><p>仅记录 SKU、动作和版本；尚未建立实名员工权限体系，无法将操作归属到个人。</p>
   <div className="table-wrap"><table><thead><tr><th>时间 UTC</th><th>SKU</th><th>版本</th><th>动作</th></tr></thead><tbody>
    {audit.map((a,i)=><tr key={a.sku+String(a.version)+String(i)}><td>{a.occurred_at}</td><td>{a.sku}</td><td>{a.version}</td><td>{a.action}</td></tr>)}
    {!audit.length&&<tr><td colSpan={4}>尚无操作记录</td></tr>}
   </tbody></table></div>
  </section>
 </div>;
}
